import BusinessIcon from '@mui/icons-material/Business'
import { Avatar, Box, LinearProgress, Typography } from '@mui/material'
import React, { useState, useEffect } from 'react'

import { useOfficeForm } from '../../hooks/useOfficeForm'
import Database from '../../types/Database'
import { OfficeForm } from '../office'

import WizardPointer from './WizardPointer'

interface CreateOfficeStepProps {
  user: Database.User
  onSuccess: (office: Database.Office) => void
  onCancel: () => void
}

/**
 * CreateOfficeStep - Wrapper component for office creation within onboarding wizard
 *
 * Wraps the existing OfficeForm component and integrates it into the wizard flow.
 * Features:
 * - Intercepts onCreate callback to proceed to next step
 * - Handles cancel to return to choice step
 * - Shows pointer on first visit for guidance
 *
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 10.1
 */
const CreateOfficeStep: React.FC<CreateOfficeStepProps> = ({
  user,
  onSuccess,
  onCancel
}) => {
  const [showPointer, setShowPointer] = useState(true)

  // Initialize the office form hook
  const {
    formData,
    isSubmitting,
    isRetrying,
    retryCount,
    validationErrors,
    successMessage,
    updateField,
    submitForm,
    clearMessage
  } = useOfficeForm({
    user,
    office: null, // Creating new office
    onCreate: office => {
      // Office created successfully - proceed to next step
      onSuccess(office)
    }
  })

  // Hide pointer when user starts interacting with the form
  useEffect(() => {
    const handleFormInteraction = () => {
      if (showPointer) {
        setShowPointer(false)
      }
    }

    // Listen for any input events on the form
    const formElement = document.querySelector('form')
    if (formElement) {
      formElement.addEventListener('input', handleFormInteraction)
      formElement.addEventListener('focus', handleFormInteraction, true)

      return () => {
        formElement.removeEventListener('input', handleFormInteraction)
        formElement.removeEventListener('focus', handleFormInteraction, true)
      }
    }
    return undefined
  }, [showPointer])

  const handleDismissPointer = () => {
    setShowPointer(false)
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', px: 2 }}>
      {/* Loading indicator for page-level operations */}
      {(isSubmitting || isRetrying) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            sx={{
              borderRadius: 1,
              height: 4,
              backgroundColor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: isRetrying ? '#ff9800' : '#3b82f6'
              }
            }}
          />
        </Box>
      )}

      {/* Office Creation Form Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <BusinessIcon fontSize='small' />
        </Avatar>
        <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
          Create Your Office
        </Typography>
      </Box>

      <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
        Set up your office space where your team can book desks and collaborate.
      </Typography>

      {/* Office Form */}
      <Box id='office-form-container'>
        <OfficeForm
          formData={formData}
          isSubmitting={isSubmitting}
          isRetrying={isRetrying}
          retryCount={retryCount}
          validationErrors={validationErrors}
          successMessage={successMessage}
          onSubmit={submitForm}
          onChange={updateField}
          onClearMessage={clearMessage}
        />
      </Box>

      {/* Cancel Button */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
        <Typography
          component='button'
          onClick={onCancel}
          sx={{
            background: 'none',
            border: 'none',
            color: 'text.secondary',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '0.875rem',
            padding: 0,
            '&:hover': {
              color: 'text.primary'
            }
          }}
        >
          Back to choices
        </Typography>
      </Box>

      {/* Wizard Pointer for first-time guidance */}
      <WizardPointer
        target='#office-form-container input[name="name"]'
        content='Start by entering your office name. This will help your team identify the workspace.'
        placement='bottom'
        visible={showPointer}
        onDismiss={handleDismissPointer}
      />
    </Box>
  )
}

export default CreateOfficeStep
